import {
    Box,
    Button,
    Grid,
    InputAdornment,
    Paper,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Info, Palette, Save } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { memo, useCallback, useEffect, useState } from "react"
import { BotaoVoltar } from "./BotaoVoltar"
import { CoresAplicativoActionsTypes } from "../context/coresAplicativoActions"
import { useCoresAplicativoContext } from "../context/useCoresAplicativoContext"
import type { CampoCor, CoresAplicativoModel } from "../models/coresAplicativoModel"
import { GRUPOS_CORES } from "../models/coresAplicativoModel"

const GRUPOS_CORES_SITE = GRUPOS_CORES.filter(grupo => grupo.titulo === "Cores do Site")

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    header: {
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down("sm")]: { marginBottom: theme.spacing(3) },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: {
            borderRadius: 15,
            marginBottom: theme.spacing(2),
        },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    label: {
        marginBottom: 6,
        fontWeight: 500,
        color: "#2c3e50",
        display: "flex",
        alignItems: "center",
    },
    infoIcon: {
        fontSize: "1rem",
        color: "#7f8c8d",
        marginLeft: 4,
        cursor: "help",
    },
    swatch: {
        width: 34,
        height: 34,
        padding: 0,
        border: "none",
        borderRadius: 6,
        background: "none",
        cursor: "pointer",
    },
    textField: {
        backgroundColor: "white",
        "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            "& fieldset": { borderColor: "#e0e0e0" },
            "&:hover fieldset": { borderColor: "#4caf50" },
            "&.Mui-focused fieldset": { borderColor: "#4caf50" },
        },
    },
    avisoSomenteLeitura: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        backgroundColor: "#fff8e1",
        border: "1px solid #ffe082",
        borderRadius: 12,
        padding: theme.spacing(1.5, 2),
        marginBottom: theme.spacing(3),
        color: "#8a6d3b",
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "center",
        marginTop: theme.spacing(4),
        [theme.breakpoints.down("sm")]: { marginTop: theme.spacing(3) },
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        padding: theme.spacing(1.5, 4),
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
        minWidth: "180px",
        "&:hover": {
            backgroundColor: "#45a049",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(76, 175, 80, 0.4)",
        },
        "&.Mui-disabled": {
            backgroundColor: "#c8e6c9",
            color: "white",
        },
        [theme.breakpoints.down("sm")]: {
            minWidth: "100%",
            padding: theme.spacing(2, 3),
        },
    },
}))

function normalizarParaPicker(valor: string): string {
    if (!valor) return "#000000"
    let v = valor.trim()
    if (!v.startsWith("#")) v = "#" + v
    if (/^#[0-9a-fA-F]{6}$/.test(v)) return v
    if (/^#[0-9a-fA-F]{3}$/.test(v)) {
        const r = v[1]
        const g = v[2]
        const b = v[3]
        return `#${r}${r}${g}${g}${b}${b}`
    }
    return "#000000"
}

type CampoCorInputProps = CampoCor & {
    valor: string
    disabled: boolean
    onCommit: (campo: CampoCor["campo"], valor: string) => void
}

const CampoCorInput = memo(function CampoCorInput({
    campo,
    label,
    descricao,
    valor,
    disabled,
    onCommit,
}: CampoCorInputProps) {
    const classes = useStyles()
    const [local, setLocal] = useState(valor)

    useEffect(() => {
        setLocal(valor)
    }, [valor])

    const commit = () => {
        if (local !== valor) onCommit(campo, local)
    }

    return (
        <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" className={classes.label}>
                {label}
                {descricao && (
                    <Tooltip title={descricao} arrow>
                        <Info className={classes.infoIcon} />
                    </Tooltip>
                )}
            </Typography>
            <TextField
                className={classes.textField}
                variant="outlined"
                size="small"
                fullWidth
                value={local}
                onChange={e => setLocal(e.target.value)}
                onBlur={commit}
                disabled={disabled}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <input
                                type="color"
                                className={classes.swatch}
                                value={normalizarParaPicker(local)}
                                onChange={e => setLocal(e.target.value)}
                                onBlur={commit}
                                disabled={disabled}
                                aria-label={`Selecionar ${label}`}
                            />
                        </InputAdornment>
                    ),
                }}
            />
        </Grid>
    )
})

export function FormularioCoresAplicativo() {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const { cores, dispatch, salvar, carregando, erroCarregamento } = useCoresAplicativoContext()
    const { enqueueSnackbar } = useSnackbar()

    const somenteLeitura = cores.podeEditar === false
    const desabilitado = !!erroCarregamento || somenteLeitura

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erroCarregamento])

    const handleCampo = useCallback(
        (campo: keyof CoresAplicativoModel, valor: string) => {
            dispatch({
                type: CoresAplicativoActionsTypes.ATUALIZAR_CAMPO,
                payload: { campo, valor },
            })
        },
        [dispatch]
    )

    const handleSalvar = async () => {
        try {
            await salvar()
            alertStart("Cores salvas com sucesso!", "success")
        } catch (error) {
            alertStart((error as Error).message || "Erro ao salvar as cores", "error")
        }
    }

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <BotaoVoltar />
                <Box className={classes.header}>
                    <Typography variant="h4" className={classes.titleText}>
                        Cores do Site
                    </Typography>
                    <Typography variant="subtitle1" style={{ color: "#7f8c8d" }}>
                        Personalize as cores do site
                    </Typography>
                </Box>

                {somenteLeitura && (
                    <Box className={classes.avisoSomenteLeitura}>
                        <Info />
                        <Typography variant="body2">
                            Este estabelecimento não possui um aplicativo próprio. As cores exibidas
                            são as padrão e não podem ser editadas aqui.
                        </Typography>
                    </Box>
                )}

                {GRUPOS_CORES_SITE.map(grupo => (
                    <Paper className={classes.configCard} key={grupo.titulo}>
                        <Box className={classes.cardHeader}>
                            <Palette className={classes.iconHeader} />
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                {grupo.titulo}
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            <Grid container spacing={isMobile ? 2 : 3}>
                                {grupo.campos.map(c => (
                                    <CampoCorInput
                                        key={c.campo}
                                        campo={c.campo}
                                        label={c.label}
                                        descricao={c.descricao}
                                        valor={(cores[c.campo] as string) ?? ""}
                                        disabled={desabilitado}
                                        onCommit={handleCampo}
                                    />
                                ))}
                            </Grid>
                        </Box>
                    </Paper>
                ))}

                <Box className={classes.buttonContainer}>
                    <Button
                        className={classes.saveButton}
                        startIcon={!isMobile && <Save />}
                        onClick={handleSalvar}
                        disabled={carregando || desabilitado}
                        size={isMobile ? "large" : "medium"}
                    >
                        {isMobile ? "💾 Salvar Cores" : "Salvar Cores"}
                    </Button>
                </Box>
            </div>
        </div>
    )
}
